import { Button, toast } from "@/components/ui";
import { splitByLineBreaks } from "@/lib/formatters";
import { isActionAvailable } from "@/utils/available-actions";
import { useState } from "react";
import type { z } from "zod";
import { useCarePlanSummary } from "../network/useCarePlanSummary";
import { CareplanAvailableAction } from "../network/useCarePlanSummary";
import { useCreateRecommendation } from "../network/useCreateRecommendation";
import { useCreateRecommendations } from "../network/useCreateRecommendations";
import { RecommendationEditor } from "./RecommendationEditor";
import type { editRecommendationSchema } from "./schema";

interface AddRecommendationProps {
  carePlanId: string;
  encounterId: string;
}

export const AddRecommendation = ({
  carePlanId,
  encounterId,
}: AddRecommendationProps) => {
  const [isAddingRecommendation, setIsAddingRecommendation] = useState(false);

  const { data: careplanSummary } = useCarePlanSummary(encounterId);
  const careplanAvailableActions = careplanSummary?.availableActions;

  const canAddRecommendation = isActionAvailable(
    CareplanAvailableAction.ADD_RECOMMENDATION,
    careplanAvailableActions,
  );

  const createRecommendation = useCreateRecommendation({
    encounterId,
    carePlanId,
  });

  const createRecommendations = useCreateRecommendations({
    encounterId,
    carePlanId,
  });

  const handleClick = () => {
    setIsAddingRecommendation(true);
  };

  const onSubmit = async (values: z.infer<typeof editRecommendationSchema>) => {
    try {
      const recommendations = splitByLineBreaks(values.text);
      if (recommendations?.length === 1) {
        await createRecommendation.mutateAsync({
          recommendationDescription: recommendations[0],
        });
      } else if (recommendations?.length > 1) {
        await createRecommendations.mutateAsync({
          recommendationDescriptions: recommendations,
        });
      }

      setIsAddingRecommendation(false);
    } catch (error) {
      toast.error("Failed to add recommendation.");
    }
  };

  if (isAddingRecommendation)
    return (
      <RecommendationEditor
        onSubmit={onSubmit}
        onCancel={() => setIsAddingRecommendation(false)}
        isSaving={
          createRecommendation.isPending || createRecommendations.isPending
        }
      />
    );

  return (
    <Button
      variant="secondary"
      size="xs"
      onClick={handleClick}
      className="w-fit mt-2"
      disabled={!canAddRecommendation}
    >
      Add Recommendation
    </Button>
  );
};
